import { Button, Checkbox, Empty, Input, Pagination, Select, Skeleton, Table, Tabs, Tag } from "antd";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "components/PageHeader";
import { BUDGET_TIER_LABEL, ERROR_CODE, TAB_LABEL, TAB_ORDER } from "features/ConsultRequest/core/constants";
import { formatDateTime } from "features/ConsultRequest/core/utils";
import { listAssignees } from "features/ConsultRequest/data/apiAdapters";
import { useConsultQuery, useConsultRequestListData, useProjectTypeOptions } from "features/ConsultRequest/data/hooks";
import { mapBudget, mapDealer, mapTimeline } from "features/ConsultRequest/data/mappers";
import AssignModal from "features/ConsultRequest/presentation/components/AssignModal";
import ConsultErrorState from "features/ConsultRequest/presentation/components/ConsultErrorState";
import StatusText from "features/ConsultRequest/presentation/components/StatusText";
import "./index.scss";

const BUDGET_FILTER_OPTIONS = [
  ...Object.entries(BUDGET_TIER_LABEL).map(([value, label]) => ({ value, label })),
  { value: "CUSTOM", label: "Ngân sách cụ thể" },
];

export default function ConsultRequestList() {
  const { t } = useTranslation();
  const vm = useConsultRequestListData();
  const { query, list, counts } = vm;
  const projectTypes = useProjectTypeOptions();
  // PW5 FORBIDDEN nghĩa là không phải leader: ẩn nút Phân công và bộ lọc người phụ trách.
  const assignees = useConsultQuery(listAssignees, [""]);
  const isLeader = Boolean(assignees.data) && assignees.error?.code !== ERROR_CODE.FORBIDDEN;
  const [assigning, setAssigning] = useState(null);
  const filters = query.filters;
  const hasFilter = Object.values(filters).some((v) => (Array.isArray(v) ? v.length : Boolean(v)));

  const columns = [
    { title: "STT", width: 58, render: (_, __, i) => (query.page - 1) * query.size + i + 1 },
    {
      title: "MÃ PHIẾU",
      width: 210,
      render: (_, r) => (
        <span>
          <Link className="consult-code" to={`/consult-request/detail/${r.id}`}>
            {r.code}
          </Link>
          {r.possibleDuplicate && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              Có thể trùng
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: "KHÁCH HÀNG",
      render: (_, r) => {
        const dealer = mapDealer(r.dealer);
        return (
          <div>
            {dealer.taxCode && <div className="consult-tax">{dealer.taxCode}</div>}
            <div>{dealer.companyName}</div>
          </div>
        );
      },
    },
    { title: "LOẠI DỰ ÁN", width: 180, dataIndex: "projectTypeName" },
    { title: "NGÂN SÁCH", width: 210, render: (_, r) => mapBudget(r) },
    { title: "THỜI GIAN TRIỂN KHAI", width: 200, render: (_, r) => mapTimeline(r) },
    { title: "THỜI GIAN GỬI", width: 220, render: (_, r) => formatDateTime(r.createdAt) },
    { title: "NV TIẾP NHẬN", width: 240, render: (_, r) => r.assignee?.name || "--" },
    { title: "TRẠNG THÁI", width: 200, render: (_, r) => <StatusText status={r.status} /> },
    {
      title: "HÀNH ĐỘNG",
      width: 140,
      render: (_, r) =>
        isLeader && r.status === "WAITING_ASSIGN" ? (
          <Button type="link" onClick={() => setAssigning(r)}>
            Phân công
          </Button>
        ) : null,
    },
  ];

  const setFilter = (patch) => vm.setFilters({ ...filters, ...patch });
  const total = list.data?.total || 0;
  const from = total ? (query.page - 1) * query.size + 1 : 0;
  const to = Math.min(query.page * query.size, total);

  let body;
  if (list.error) body = <ConsultErrorState error={list.error} onRetry={list.refetch} />;
  else if (list.loading && !list.data) body = <Skeleton active paragraph={{ rows: 8 }} />;
  else
    body = (
      <>
        <Table
          className="consult-table"
          rowKey="id"
          loading={list.loading}
          columns={columns}
          dataSource={list.data?.items || []}
          pagination={false}
          locale={{
            emptyText: hasFilter ? (
              <Empty description="Không tìm thấy phiếu phù hợp">
                <Button onClick={() => vm.setFilters({})}>Xóa bộ lọc</Button>
              </Empty>
            ) : (
              <Empty description="Không có phiếu nào ở trạng thái này" />
            ),
          }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 16 }}>
          <span className="consult-pagination-info">
            {`Hiển thị ${from} - ${to} của ${total} (${Math.ceil(total / query.size)} trang)`}
          </span>
          <Pagination
            current={query.page}
            pageSize={query.size}
            total={total}
            showSizeChanger={false}
            onChange={(page) => vm.setPage(page)}
          />
        </div>
      </>
    );

  return (
    <div className="consult-list-page">
      <PageHeader
        pageTitle={t("v2.consultRequest.title")}
        routes={[
          { name: t("v2.consultRequest.breadcrumbRoot"), path: "/seller" },
          { name: t("v2.consultRequest.title") },
        ]}
      />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "12px 0" }}>
        <Input.Search
          allowClear
          style={{ width: 260 }}
          placeholder="Mã phiếu / công ty / MST"
          onSearch={(keyword) => setFilter({ keyword })}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 200 }}
          placeholder="Loại dự án"
          value={filters.projectTypeIds || []}
          onChange={(projectTypeIds) => setFilter({ projectTypeIds })}
          options={(projectTypes.data || []).map((o) => ({
            value: o.id,
            label: o.isVisible ? o.name : `${o.name} (đã ẩn)`,
          }))}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 200 }}
          placeholder="Ngân sách"
          value={filters.budgetFilter || []}
          onChange={(budgetFilter) => setFilter({ budgetFilter })}
          options={BUDGET_FILTER_OPTIONS}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 200 }}
          placeholder="Thời gian triển khai"
          value={filters.timelines || []}
          onChange={(timelines) => setFilter({ timelines })}
          options={[
            { value: "URGENT_WEEK", label: "Gấp trong tuần" },
            { value: "IN_MONTH", label: "Trong tháng" },
            { value: "ONE_TO_THREE_MONTHS", label: "1–3 tháng" },
            { value: "SCHEDULED", label: "Hẹn lịch cụ thể" },
          ]}
        />
        <Input
          type="date"
          style={{ width: 150 }}
          value={filters.createdFrom || ""}
          onChange={(e) => setFilter({ createdFrom: e.target.value })}
        />
        <Input
          type="date"
          style={{ width: 150 }}
          value={filters.createdTo || ""}
          onChange={(e) => setFilter({ createdTo: e.target.value })}
        />
        {isLeader && (
          <Select
            allowClear
            style={{ minWidth: 180 }}
            placeholder="Người phụ trách"
            value={filters.assigneeId}
            onChange={(assigneeId) => setFilter({ assigneeId })}
            options={(assignees.data || []).map((a) => ({ value: a.userId, label: a.name }))}
          />
        )}
        <Checkbox
          checked={Boolean(filters.possibleDuplicate)}
          onChange={(e) => setFilter({ possibleDuplicate: e.target.checked })}
        >
          Có thể trùng
        </Checkbox>
      </div>
      <Tabs className="consult-list-tabs" activeKey={query.status} onChange={vm.setStatus}>
        {TAB_ORDER.map((key) => (
          <Tabs.TabPane key={key} tab={`${TAB_LABEL[key]} (${counts.data?.[key] ?? 0})`} />
        ))}
      </Tabs>
      {body}
      <AssignModal
        visible={Boolean(assigning)}
        request={assigning}
        onClose={() => setAssigning(null)}
        onAssigned={vm.reload}
        onStale={list.refetch}
      />
    </div>
  );
}
